import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";
import { Trash2, Plus, ArrowUpDown, CheckCircle2, XCircle, ExternalLink } from "lucide-react";

type PatternKind = "subdomain" | "domain" | "host_exact";

interface BlacklistRow {
  id: string;
  pattern: string;
  pattern_kind: string;
  enabled: boolean;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

const KIND_META: Record<PatternKind, { label: string; help: string }> = {
  subdomain: {
    label: "Subdomain prefixes",
    help: "Matches the leading host label. e.g. 'support' matches support.apple.com, support.x.com.",
  },
  domain: {
    label: "Domains (and all subdomains)",
    help: "e.g. 'apple.com' matches apple.com and mail.apple.com but NOT evilapple.com.",
  },
  host_exact: {
    label: "Exact hosts",
    help: "Matches one full hostname only. e.g. 'www.gfiber.com'.",
  },
};

function formatDate(s: string) {
  const d = new Date(s);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

export default function ResearchUrlBlacklist() {
  const { enabled: flagOn, isLoading: flagLoading } = useFeatureFlag("admin_url_blacklist");
  const { user } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState<PatternKind>("subdomain");

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["research_url_blacklist"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("research_url_blacklist")
        .select("*")
        .order("pattern", { ascending: true });
      if (error) throw error;
      return data as BlacklistRow[];
    },
    enabled: flagOn,
  });

  if (flagLoading) return <div className="p-6 text-muted-foreground">Loading…</div>;
  if (!flagOn) {
    return (
      <div className="p-6 text-muted-foreground">
        The URL blacklist admin screen is disabled. Enable the <code>admin_url_blacklist</code> feature flag to access it.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div className="max-w-3xl">
          <h2 className="text-2xl font-bold text-primary">URL Blacklist</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Blocks pages from being ingested by Firecrawl, Gmail link fan-out, and KG extraction. Edits take effect
            immediately — no deploy required. Changes here directly throttle ingest API cost.
          </p>
        </div>
        <AddRuleDialog defaultKind={tab} userId={user?.id ?? null} onSaved={() => qc.invalidateQueries({ queryKey: ["research_url_blacklist"] })} />
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as PatternKind)}>
        <TabsList>
          {(Object.keys(KIND_META) as PatternKind[]).map((k) => {
            const count = rows.filter((r) => r.pattern_kind === k).length;
            return (
              <TabsTrigger key={k} value={k}>
                {KIND_META[k].label} <span className="ml-2 text-xs opacity-70">({count})</span>
              </TabsTrigger>
            );
          })}
        </TabsList>
        {(Object.keys(KIND_META) as PatternKind[]).map((k) => (
          <TabsContent key={k} value={k} className="space-y-3">
            <p className="text-xs text-muted-foreground">{KIND_META[k].help}</p>
            <BlacklistTable
              rows={rows.filter((r) => r.pattern_kind === k)}
              isLoading={isLoading}
              kind={k}
            />
          </TabsContent>
        ))}
      </Tabs>

      <div className="border rounded-md p-4 bg-muted/30">
        <div className="flex items-center justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold">What's been blocked</h3>
            <p className="text-xs text-muted-foreground">
              Coming soon — for now, check edge-function logs for <code>skipped: 'url_blacklisted'</code>.
            </p>
          </div>
          <a
            className="text-xs inline-flex items-center gap-1 text-primary hover:underline"
            href="https://supabase.com/dashboard/project/ewyfhrjexfrmcjhqzsmm/functions/research-ingest-web/logs"
            target="_blank"
            rel="noreferrer"
          >
            Open logs <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>
    </div>
  );
}

function BlacklistTable({
  rows,
  isLoading,
  kind,
}: {
  rows: BlacklistRow[];
  isLoading: boolean;
  kind: PatternKind;
}) {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<"pattern" | "created_at">("pattern");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q
      ? rows.filter(
          (r) =>
            r.pattern.toLowerCase().includes(q) ||
            (r.notes ?? "").toLowerCase().includes(q)
        )
      : rows;
    const sorted = [...base].sort((a, b) => {
      const av = sortBy === "pattern" ? a.pattern : a.created_at;
      const bv = sortBy === "pattern" ? b.pattern : b.created_at;
      return (av > bv ? 1 : av < bv ? -1 : 0) * (sortDir === "asc" ? 1 : -1);
    });
    return sorted;
  }, [rows, search, sortBy, sortDir]);

  const toggleSort = (col: "pattern" | "created_at") => {
    if (sortBy === col) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
  };

  const invalidate = () => qc.invalidateQueries({ queryKey: ["research_url_blacklist"] });

  return (
    <div className="space-y-3">
      <Input
        placeholder="Search pattern or notes…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="max-w-sm"
      />
      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <button className="inline-flex items-center gap-1" onClick={() => toggleSort("pattern")}>
                  Pattern <ArrowUpDown className="h-3 w-3" />
                </button>
              </TableHead>
              <TableHead className="w-24">Enabled</TableHead>
              <TableHead>Notes</TableHead>
              <TableHead className="w-40">
                <button className="inline-flex items-center gap-1" onClick={() => toggleSort("created_at")}>
                  Last edited <ArrowUpDown className="h-3 w-3" />
                </button>
              </TableHead>
              <TableHead className="w-16"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={5} className="text-muted-foreground text-sm">Loading…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-muted-foreground text-sm">
                {rows.length === 0 ? "No rules. Add one via the button top-right." : "No matches."}
              </TableCell></TableRow>
            ) : filtered.map((r) => (
              <RowEditor key={r.id} row={r} onChanged={invalidate} />
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function RowEditor({ row, onChanged }: { row: BlacklistRow; onChanged: () => void }) {
  const [notes, setNotes] = useState(row.notes ?? "");
  const [busy, setBusy] = useState(false);

  const saveNotes = async () => {
    if ((row.notes ?? "") === notes) return;
    setBusy(true);
    const { error } = await supabase.from("research_url_blacklist").update({ notes: notes || null }).eq("id", row.id);
    setBusy(false);
    if (error) {
      toast({ title: "Could not save notes", description: error.message, variant: "destructive" });
      setNotes(row.notes ?? "");
    } else {
      toast({ title: "Notes saved" });
      onChanged();
    }
  };

  const toggleEnabled = async (checked: boolean) => {
    const { error } = await supabase.from("research_url_blacklist").update({ enabled: checked }).eq("id", row.id);
    if (error) {
      toast({ title: "Could not update", description: error.message, variant: "destructive" });
    } else {
      onChanged();
    }
  };

  const remove = async () => {
    const { error } = await supabase.from("research_url_blacklist").delete().eq("id", row.id);
    if (error) toast({ title: "Could not delete", description: error.message, variant: "destructive" });
    else { toast({ title: "Rule deleted" }); onChanged(); }
  };

  return (
    <TableRow>
      <TableCell className="font-mono text-sm">{row.pattern}</TableCell>
      <TableCell>
        <Switch checked={row.enabled} onCheckedChange={toggleEnabled} />
      </TableCell>
      <TableCell>
        <Input
          value={notes}
          disabled={busy}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={saveNotes}
          placeholder="Add a note…"
          className="h-8"
        />
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">{formatDate(row.updated_at)}</TableCell>
      <TableCell>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Delete">
              <Trash2 className="h-4 w-4" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Permanently delete this rule?</AlertDialogTitle>
              <AlertDialogDescription>
                Existing ingests won't be affected; this only changes future filtering.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={remove}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </TableCell>
    </TableRow>
  );
}

function AddRuleDialog({
  defaultKind,
  userId,
  onSaved,
}: {
  defaultKind: PatternKind;
  userId: string | null;
  onSaved: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [pattern, setPattern] = useState("");
  const [kind, setKind] = useState<PatternKind>(defaultKind);
  const [notes, setNotes] = useState("");
  const [testUrl, setTestUrl] = useState("");
  const [testResult, setTestResult] = useState<null | { ok: boolean; url: string }>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const reset = () => {
    setPattern(""); setKind(defaultKind); setNotes(""); setTestUrl(""); setTestResult(null);
  };

  const runTest = async () => {
    if (!testUrl.trim()) return;
    setTesting(true);
    const { data, error } = await supabase.rpc("_research_url_is_blacklisted", { _url: testUrl.trim() });
    setTesting(false);
    if (error) {
      toast({ title: "Test failed", description: error.message, variant: "destructive" });
      return;
    }
    setTestResult({ ok: !!data, url: testUrl.trim() });
  };

  const save = async () => {
    if (!pattern.trim()) return;
    setSaving(true);
    const { error } = await supabase.from("research_url_blacklist").insert({
      pattern: pattern.trim(),
      pattern_kind: kind,
      notes: notes.trim() || null,
      created_by: userId,
    });
    setSaving(false);
    if (error) {
      toast({ title: "Could not save rule", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Rule added" });
    onSaved();
    reset();
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); else setKind(defaultKind); }}>
      <DialogTrigger asChild>
        <Button><Plus className="h-4 w-4" /> Add rule</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add blacklist rule</DialogTitle>
          <DialogDescription>
            Block URLs from being fetched by ingest, Gmail fan-out and KG extraction.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="pattern">Pattern</Label>
            <Input id="pattern" value={pattern} onChange={(e) => setPattern(e.target.value)} placeholder="support" />
            <p className="text-xs text-muted-foreground">
              e.g. 'support' (matches support.apple.com) or 'apple.com' (matches all *.apple.com).
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>Pattern kind</Label>
            <RadioGroup value={kind} onValueChange={(v) => setKind(v as PatternKind)}>
              {(Object.keys(KIND_META) as PatternKind[]).map((k) => (
                <div key={k} className="flex items-center gap-2">
                  <RadioGroupItem value={k} id={`kind-${k}`} />
                  <Label htmlFor={`kind-${k}`} className="font-normal cursor-pointer">
                    {KIND_META[k].label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea id="notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              placeholder="Short rationale for future Sam." />
          </div>

          <div className="space-y-1.5 border-t pt-4">
            <Label htmlFor="testUrl">Test against URL (optional)</Label>
            <div className="flex gap-2">
              <Input id="testUrl" value={testUrl} onChange={(e) => { setTestUrl(e.target.value); setTestResult(null); }}
                placeholder="https://support.apple.com/en-gb/..." />
              <Button type="button" variant="outline" onClick={runTest} disabled={testing || !testUrl.trim()}>
                {testing ? "Testing…" : "Test"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Tests the URL against ALL currently enabled rules (not just this draft). Save first to test the new rule.
            </p>
            {testResult && (
              <div className={`flex items-center gap-2 text-sm ${testResult.ok ? "text-green-600" : "text-destructive"}`}>
                {testResult.ok
                  ? <><CheckCircle2 className="h-4 w-4" /> Blocked by an existing rule.</>
                  : <><XCircle className="h-4 w-4" /> Not currently blocked.</>}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} disabled={!pattern.trim() || saving}>{saving ? "Saving…" : "Save rule"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
